import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Briefcase } from "lucide-react";
import { toast } from "sonner";

import { useCareersForm } from "./careers/useCareersForm";
import { AboutYouSection } from "./careers/AboutYouSection";
import { RoleSection } from "./careers/RoleSection";
import { ExperienceSection } from "./careers/ExperienceSection";
import { ReferenceSection } from "./careers/ReferenceSection";
import { InterviewSection } from "./careers/InterviewSection";
import { CoverSection } from "./careers/CoverSection";
import { MissingBanner, ServerErrorBanner } from "./careers/CareersBanners";
import { EmploymentPolicies } from "./careers/EmploymentPolicies";

function scrollTo(id) {
  setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
}

export default function Careers() {
  const location = useLocation();
  const { form, set, missing, serverError, setServerError, validate, isMissing } = useCareersForm(location.key);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/site-content/careers").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    const missed = validate();
    if (missed.length > 0) {
      toast.error(`Please answer ${missed.length} required question${missed.length > 1 ? "s" : ""} before submitting.`);
      scrollTo("careers-missing-banner");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/careers/apply", {
        ...form,
        years_experience: parseInt(form.years_experience || 0),
        desired_hourly_salary: parseFloat(form.desired_hourly_salary || 0),
      });
      setSubmitted(true);
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || err.message || "";
      setServerError(detail);
      toast.error("We couldn't submit your application. Please call 718-216-2013 or email info@sandarsushi.com.");
      scrollTo("careers-server-error");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="careers-thankyou">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-6"/>
        <h1 className="font-display font-bold text-4xl tracking-tight">Thank you!</h1>
        <p className="mt-3 text-neutral-500">Your application has been received. We'll be in touch soon.</p>
      </div>
    );
  }

  return (
    <div>
      <section className="relative h-72 overflow-hidden">
        <img src={content?.hero_image_url || "https://images.unsplash.com/photo-1512132411229-c30391241dd8?auto=format&fit=crop&w=2000&q=80"} alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"/>
        <div className="absolute bottom-10 left-0 right-0 max-w-7xl mx-auto px-6 lg:px-10">
          <p className="eyebrow text-white/80 mb-2" data-testid="careers-hero-eyebrow">{content?.hero_eyebrow ?? "Careers"}</p>
          <h1 className="font-display font-bold text-5xl text-white tracking-tight" data-testid="careers-hero-title">
            {content?.hero_title_line1 ?? "Roll"} {content?.hero_title_line2 ?? "with us."}
          </h1>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-12 careers-form">
        {/* Job posting callout */}
        <div className="mb-8 rounded-2xl bg-neutral-950 text-white p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[hsl(var(--primary))]/20"/>
          <div className="absolute right-16 -bottom-6 w-24 h-24 rounded-full bg-[hsl(var(--primary))]/10"/>
          <div className="relative">
            <span className="eyebrow text-[hsl(var(--primary))]" data-testid="careers-job-tag">{content?.job_tag ?? "Now Hiring"}</span>
            <h2 className="font-display font-bold text-2xl sm:text-3xl mt-2 tracking-tight" data-testid="careers-job-title">
              {content?.job_title ?? "Experienced Sushi & Ramen Chefs"}
            </h2>
            <p className="text-neutral-300 mt-3 leading-relaxed max-w-2xl whitespace-pre-line" data-testid="careers-job-blurb">
              {content?.job_blurb ?? "We are looking for experienced Sushi & Ramen Chefs to join the SANDAR Sushi team at our food-court location."}
            </p>
          </div>
        </div>

        <EmploymentPolicies />

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))]/10 flex items-center justify-center"><Briefcase className="w-5 h-5 text-[hsl(var(--primary))]"/></div>
          <p className="text-neutral-600 leading-relaxed whitespace-pre-line" data-testid="careers-intro-note">
            {content?.intro_note ?? "Tell us about yourself — most questions are optional. Fields marked with * are required."}
          </p>
        </div>

        <MissingBanner missing={missing} />
        <ServerErrorBanner serverError={serverError} />

        <form onSubmit={submit} className="space-y-6" data-testid="careers-form" noValidate>
          <AboutYouSection form={form} set={set} isMissing={isMissing} />
          <RoleSection form={form} set={set} isMissing={isMissing} />
          <ExperienceSection form={form} set={set} />
          <ReferenceSection form={form} set={set} />
          <InterviewSection form={form} set={set} isMissing={isMissing} />
          <CoverSection form={form} set={set} />

          <Button type="submit" disabled={submitting} className="w-full h-12 rounded-full text-base bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="careers-submit">
            {submitting ? "Sending…" : "Submit Application"}
          </Button>
        </form>
      </section>
    </div>
  );
}
